import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, exportUrl, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Eye, Pencil, Trash2, FileSpreadsheet, FileDown, FilterX } from "lucide-react";

function FilterSelect({ label, param, options, value, onChange, testid }) {
  const { t } = useLang();
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(param, v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-9 text-xs bg-white">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}: {t("all")}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const YES_NO = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }];

export default function Families() {
  const { t } = useLang();
  const { scope } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(searchParams.entries());
      const { data } = await api.get("/families", { params: { ...params, scope } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [searchParams, scope]);

  useEffect(() => {
    load();
  }, [load]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const currentParams = Object.fromEntries(searchParams.entries());
  const exportParams = { ...currentParams, scope };

  const doDelete = async () => {
    try {
      await api.delete(`/families/${deleteTarget}`, { params: { scope } });
      toast.success(t("deleted"));
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const yesNo = (v) => (v ? <Badge className="bg-agri-light text-agri border-agri/20" variant="outline">{t("yes")}</Badge> : <Badge variant="outline" className="text-slate-400">{t("no")}</Badge>);

  return (
    <div className="space-y-5" data-testid="families-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-semibold text-graphite" data-testid="families-title">{t("families_title")}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {data ? `${data.count} ${t("results")}` : t("families_subtitle")}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap no-print">
          <Button data-testid="export-excel-btn" variant="outline" size="sm" onClick={() => window.open(exportUrl("/export/excel", exportParams), "_blank")}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5 text-agri" />{t("export_excel")}
          </Button>
          <Button data-testid="export-pdf-btn" variant="outline" size="sm" onClick={() => window.open(exportUrl("/export/pdf", exportParams), "_blank")}>
            <FileDown className="h-4 w-4 mr-1.5 text-brand-red" />{t("export_pdf")}
          </Button>
          <Button data-testid="add-family-btn" size="sm" className="bg-brand-red hover:bg-brand-reddark text-white transition-colors duration-200" onClick={() => navigate("/families/new")}>
            <Plus className="h-4 w-4 mr-1.5" />{t("add_family")}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 no-print" data-testid="filters-panel">
        <div className="flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="families-search-input"
              className="pl-9 h-9"
              placeholder={t("search_placeholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setParam("search", searchInput)}
            />
          </div>
          <Button data-testid="families-search-btn" variant="outline" size="sm" className="h-9" onClick={() => setParam("search", searchInput)}>
            {t("search")}
          </Button>
          {Object.keys(currentParams).length > 0 && (
            <Button
              data-testid="clear-filters-btn"
              variant="ghost"
              size="sm"
              className="h-9 text-brand-red"
              onClick={() => { setSearchParams({}); setSearchInput(""); }}
            >
              <FilterX className="h-4 w-4 mr-1" />{t("clear_filters")}
            </Button>
          )}
        </div>
        {meta && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-2">
            <FilterSelect label={t("filter_ward")} param="ward" value={currentParams.ward} onChange={setParam} testid="filter-ward"
              options={meta.wards.map((w) => ({ value: w, label: w }))} />
            <FilterSelect label={t("filter_caste")} param="caste" value={currentParams.caste} onChange={setParam} testid="filter-caste"
              options={meta.castes.map((c) => ({ value: c, label: c }))} />
            <FilterSelect label={t("filter_own_house")} param="own_house" value={currentParams.own_house} onChange={setParam} testid="filter-own-house" options={YES_NO} />
            <FilterSelect label={t("filter_patta")} param="patta_issued" value={currentParams.patta_issued} onChange={setParam} testid="filter-patta" options={YES_NO} />
            <FilterSelect label={t("filter_needs_patta")} param="needs_patta" value={currentParams.needs_patta} onChange={setParam} testid="filter-needs-patta" options={YES_NO} />
            <FilterSelect label={t("filter_owns_land")} param="owns_land" value={currentParams.owns_land} onChange={setParam} testid="filter-owns-land" options={YES_NO} />
            <FilterSelect label={t("filter_has_borewell")} param="has_borewell" value={currentParams.has_borewell} onChange={setParam} testid="filter-has-borewell" options={YES_NO} />
            <FilterSelect label={t("filter_bw_type")} param="borewell_type" value={currentParams.borewell_type} onChange={setParam} testid="filter-bw-type"
              options={meta.borewell_types.map((b) => ({ value: b, label: b }))} />
            <FilterSelect label={t("filter_bw_status")} param="borewell_status" value={currentParams.borewell_status} onChange={setParam} testid="filter-bw-status"
              options={meta.borewell_statuses.map((b) => ({ value: b, label: b }))} />
            <FilterSelect label={t("filter_bw_electricity")} param="bw_electricity" value={currentParams.bw_electricity} onChange={setParam} testid="filter-bw-electricity" options={YES_NO} />
            <FilterSelect label={t("filter_needs_borewell")} param="needs_borewell" value={currentParams.needs_borewell} onChange={setParam} testid="filter-needs-borewell" options={YES_NO} />
            <FilterSelect label={t("filter_crop")} param="crop" value={currentParams.crop} onChange={setParam} testid="filter-crop"
              options={meta.crops.map((c) => ({ value: c, label: c }))} />
            <FilterSelect label={t("filter_season")} param="season" value={currentParams.season} onChange={setParam} testid="filter-season"
              options={meta.seasons.map((s) => ({ value: s, label: s }))} />
            <FilterSelect label={t("filter_irrigation")} param="irrigation_source" value={currentParams.irrigation_source} onChange={setParam} testid="filter-irrigation"
              options={meta.irrigation_sources.map((s) => ({ value: s, label: s }))} />
            <FilterSelect label={t("filter_education")} param="education" value={currentParams.education} onChange={setParam} testid="filter-education"
              options={meta.education.map((e) => ({ value: e, label: e }))} />
            <FilterSelect label={t("filter_profession")} param="profession" value={currentParams.profession} onChange={setParam} testid="filter-profession"
              options={meta.professions.map((p) => ({ value: p, label: p }))} />
            <FilterSelect label={t("filter_age_group")} param="age_group" value={currentParams.age_group} onChange={setParam} testid="filter-age-group"
              options={[{ value: "child", label: t("age_child") }, { value: "adult", label: t("age_adult") }, { value: "senior", label: t("age_senior") }]} />
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="families-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs">{t("col_family_id")}</TableHead>
              <TableHead className="text-xs">{t("col_head")}</TableHead>
              <TableHead className="text-xs">{t("col_ward")}</TableHead>
              <TableHead className="text-xs text-center">{t("col_members")}</TableHead>
              <TableHead className="text-xs">{t("col_caste")}</TableHead>
              <TableHead className="text-xs">{t("col_own_house")}</TableHead>
              <TableHead className="text-xs text-right">{t("col_land")}</TableHead>
              <TableHead className="text-xs text-center">{t("col_borewells")}</TableHead>
              <TableHead className="text-xs">{t("col_crops")}</TableHead>
              <TableHead className="text-xs text-right no-print">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.families?.map((f) => (
              <TableRow key={f.family_id} className="report-row hover:bg-slate-50 cursor-pointer" data-testid={`family-row-${f.family_id}`}
                onClick={() => navigate(`/families/${f.family_id}`)}>
                <TableCell className="font-mono text-xs font-medium text-water">{f.family_id}</TableCell>
                <TableCell className="text-sm font-medium text-graphite">{f.head_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{f.ward}</TableCell>
                <TableCell className="text-center text-sm">{f.member_count}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{f.caste}</Badge></TableCell>
                <TableCell>{yesNo(f.own_house)}</TableCell>
                <TableCell className="text-right text-sm">{f.total_land_acres || "—"}</TableCell>
                <TableCell className="text-center text-sm">{f.borewell_count || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-[140px] truncate">{f.crops.join(", ") || "—"}</TableCell>
                <TableCell className="text-right no-print" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`view-family-${f.family_id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/families/${f.family_id}`)}>
                      <Eye className="h-4 w-4 text-water" />
                    </Button>
                    <Button data-testid={`edit-family-${f.family_id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/families/${f.family_id}/edit`)}>
                      <Pencil className="h-4 w-4 text-graphite" />
                    </Button>
                    <Button data-testid={`delete-family-${f.family_id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteTarget(f.family_id)}>
                      <Trash2 className="h-4 w-4 text-brand-red" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.families.length === 0 && (
          <div className="py-16 text-center text-sm text-slate-400" data-testid="no-families-message">{t("no_families")}</div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget} — {t("delete_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" className="bg-brand-red hover:bg-brand-reddark text-white" onClick={doDelete}>
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
